import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { CompanyInfo, CompanyService } from '../../../services/company.service';

/** One tappable contact row. */
interface ContactRow {
  key: string;
  icon: string;       // Font Awesome 4 class (same set as the contact page)
  label: string;
  value: string;
  ltr: boolean;
  href: string;
  external: boolean;
}

interface HourRow { day: string; hours: string; closed: boolean; }

/**
 * Adaline answer to "יצירת קשר": every contact channel the system has, from
 * GET company-api/profile/info/ (the same CompanyInfo the /contact page shows,
 * edited by the admin) — phone, WhatsApp, email, address, business hours and
 * social links. Nothing is hardcoded; a channel that is empty is not shown.
 */
@Component({
  selector: 'app-adaline-contact-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './adaline-contact-card.component.html',
  styleUrls: ['./adaline-contact-card.component.scss'],
})
export class AdalineContactCardComponent implements OnInit, OnDestroy {
  /** "שלח הודעה" — the parent navigates to /contact and closes the panel. */
  @Output() openContactForm = new EventEmitter<void>();

  state: 'loading' | 'error' | 'ready' = 'loading';
  rows: ContactRow[] = [];
  hours: HourRow[] = [];
  social: ContactRow[] = [];

  private sub?: Subscription;

  /** FA4 icon per social platform (same map as ContactComponent). */
  private static readonly SOCIAL_ICONS: Record<string, string> = {
    instagram: 'fa-instagram', facebook: 'fa-facebook', tiktok: 'fa-music', youtube: 'fa-youtube',
    linkedin: 'fa-linkedin', twitter: 'fa-twitter', whatsapp: 'fa-whatsapp', telegram: 'fa-paper-plane',
  };

  constructor(private company: CompanyService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void { this.load(); }
  ngOnDestroy(): void { this.sub?.unsubscribe(); }

  load(): void {
    this.sub?.unsubscribe();
    this.state = 'loading';
    this.cdr.markForCheck();
    this.sub = this.company.getCompanyInfo().pipe(timeout(10000)).subscribe({
      next: info => { this.build(info); this.state = 'ready'; this.cdr.markForCheck(); },
      error: () => { this.state = 'error'; this.cdr.markForCheck(); },
    });
  }

  get isEmpty(): boolean {
    return !this.rows.length && !this.hours.length && !this.social.length;
  }

  private build(c: CompanyInfo): void {
    const rows: ContactRow[] = [];
    const phone = (c?.phone || '').trim();
    const wa = (c?.whatsapp || '').trim();
    const email = (c?.email || '').trim();
    if (phone) rows.push({ key: 'phone', icon: 'fa-phone', label: 'טלפון', value: this.formatPhone(phone), ltr: true, href: `tel:${phone.replace(/[^\d+]/g, '')}`, external: false });
    if (wa) rows.push({ key: 'wa', icon: 'fa-whatsapp', label: 'WhatsApp', value: this.formatPhone(wa), ltr: true, href: `https://wa.me/${this.waDigits(wa)}`, external: true });
    if (email) rows.push({ key: 'mail', icon: 'fa-envelope-o', label: 'אימייל', value: email, ltr: true, href: `mailto:${email}`, external: false });
    const address = [c?.address, c?.city].map(x => (x || '').trim()).filter(Boolean).join(', ');
    if (address) rows.push({ key: 'addr', icon: 'fa-map-marker', label: 'כתובת', value: address, ltr: false, href: `https://maps.google.com/?q=${encodeURIComponent(address)}`, external: true });
    const site = (c?.website || '').trim();
    if (site) rows.push({ key: 'web', icon: 'fa-globe', label: 'אתר', value: site.replace(/^https?:\/\//, '').replace(/\/$/, ''), ltr: true, href: /^https?:/.test(site) ? site : `https://${site}`, external: true });
    this.rows = rows;

    // Israeli week order, same as the app's CompanyModel (Sun -> Sat).
    const order = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const rank = (d: string) => { const i = order.indexOf((d || '').toLowerCase()); return i < 0 ? 99 : i; };
    this.hours = [...(c?.business_hours || [])].sort((a, b) => rank(a.day) - rank(b.day)).map(h => ({
      day: h.day_display || h.day,
      closed: !!h.is_closed,
      hours: h.is_closed ? 'סגור' : [this.hhmm(h.open_time), this.hhmm(h.close_time)].filter(Boolean).join(' – ') || '—',
    }));

    this.social = (c?.social_links || [])
      .filter(l => l.is_active !== false && !!l.url)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(l => ({
        key: 'soc-' + l.id,
        icon: AdalineContactCardComponent.SOCIAL_ICONS[(l.platform || '').toLowerCase()] || 'fa-globe',
        label: l.platform_display || l.platform,
        value: '',
        ltr: false,
        href: l.url,
        external: true,
      }));
  }

  private hhmm(t: string | null): string {
    if (!t) return '';
    const p = t.split(':');
    return p.length >= 2 ? `${p[0]}:${p[1]}` : t;
  }

  /** Israeli local number -> international digits for wa.me (05x... -> 9725x...). */
  private waDigits(n: string): string {
    const d = n.replace(/\D/g, '');
    return d.startsWith('0') ? '972' + d.slice(1) : d;
  }

  /** 0501234567 -> 050-123-4567; anything else unchanged. */
  private formatPhone(n: string): string {
    const d = n.replace(/\D/g, '');
    if (d.length === 10 && d.startsWith('0')) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
    if (d.length === 9 && d.startsWith('0')) return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`;
    return n;
  }
}
